import { dynamicPageCustomRouterCreatePageClicked } from '@wix/bi-logger-wix-code-data/v2'
import { runTransactionWithRetry } from '@wix/wix-data-client-common/dist/esm/business-logic/editor-sdk/runTransactionWithRetry'
import { addPattern } from '@wix/wix-data-client-common/dist/esm/business-logic/dynamic-pages/router-config/routerConfig'
import * as UNDO_REDO_LABELS from '@wix/wix-data-client-common/dist/esm/undoRedoLabels'
import { PAGE_TYPES } from '@wix/wix-data-client-common/dist/esm/business-data/dynamicPages'
import addDPLinkToSiteMenu from '../addDPLinkToSiteMenu'
import { isDynamicRoute } from '../isDynamicRoute'
import { getFedopsInteractionName } from '../getFedopsInteractionName'
import { addPageLinkField } from '@wix/wix-data-client-common/dist/esm/business-logic/dynamic-pages/page-link-field/editorActions'
import { getDataForNewPage } from './getDataForNewPage'
import { withProgressBar } from '../utils/withProgressBar'
import { defaultPanelBiParams } from '../../../worker/defaultBiParams'
import { ensurePageIsLegacyIndexable } from '../ensurePageIsLegacyIndexable'
import { shouldUseSeoV2RouteConfig } from '../useSeoV2Infra'
import { duplicateSeoSchema } from './duplicateSeoSchema'
import {
  isEditor3,
  setDynamicPageTypeStateForPage,
  getCollectionType,
} from '@wix/wix-data-client-common'
import { getDynamicPageType } from '../getDynamicPageType'

const duplicatePageAndConnectToRouter = async ({
  editorSdkProxy,
  pageRef,
  routerPrefix,
  route,
  routeDefinition,
  pageTitle,
}) => {
  const routerPromise = editorSdkProxy.routers.add({
    prefix: routerPrefix,
    config: addPattern({}, route, routeDefinition),
  })

  const duplicatePagePromise = editorSdkProxy.document.pages.duplicate({
    pageId: pageRef.id,
    shouldAddMenuItem: true, // ensures page appears in Pages Panel in case it was ever converted to static page
    shouldDuplicatePageCode: true,
  })

  const [routerRef, newPageRef] = await Promise.all([
    routerPromise,
    duplicatePagePromise,
  ])

  await editorSdkProxy.routers.pages.connect({
    routerRef,
    pageRef: newPageRef,
    pageRoles: [routeDefinition.pageRole],
  })

  await editorSdkProxy.pages.rename({ pageRef: newPageRef, title: pageTitle })

  await ensurePageIsLegacyIndexable({
    editorSdkProxy,
    pageRef: newPageRef,
  })

  return { newPageRef }
}

const updatePagePanelPagesList = editorSdkProxy =>
  editorSdkProxy.document.application.reloadManifest()

const duplicatePage = async ({
  editorSdkProxy,
  httpClient,
  pageRef,
  origin,
  collectionsApi,
  logger,
  i18n,
  experimentsManager,
  appApiPromise,
  editorType,
}) => {
  const useSeoV2RouteConfig = await shouldUseSeoV2RouteConfig({
    editorSdkProxy,
    experimentsManager,
  })

  const { route, routerPrefix, routeDefinition, pageTitle, menuItemLabel } =
    await getDataForNewPage({
      editorSdkProxy,
      i18n,
      pageRef,
      seoV2: useSeoV2RouteConfig,
    })

  const collectionId = routeDefinition.config.collection

  const result = await runTransactionWithRetry(
    editorSdkProxy,
    () =>
      duplicatePageAndConnectToRouter({
        editorSdkProxy,
        pageRef,
        routerPrefix,
        route,
        routeDefinition,
        pageTitle,
      }),
    true,
  )

  await updatePagePanelPagesList(editorSdkProxy)

  const navigateToPagePromise = editorSdkProxy.pages.navigateTo({
    pageRef: result.newPageRef,
  })

  const handleChangedRoutersOrAddPageLinkPromise = isEditor3(editorType)
    ? (await appApiPromise).handleChangedRouters({
        pageRefs: [result.newPageRef],
      })
    : addPageLinkField({
        collectionsApi,
        routerPrefix,
        route,
        collectionId,
        pageId: result.newPageRef.id,
        displayName: pageTitle,
      })

  const isStaticRoute = !isDynamicRoute({ route })

  const duplicateSeoSchemaPromise = routeDefinition.config.seoV2
    ? duplicateSeoSchema({
        httpClient,
        editorSdkProxy,
        oldPageRef: pageRef,
        newPageRef: result.newPageRef,
        isListPage: isStaticRoute,
      })
    : Promise.resolve()

  await Promise.all([
    navigateToPagePromise,
    handleChangedRoutersOrAddPageLinkPromise,
    duplicateSeoSchemaPromise,
  ])

  if (isStaticRoute) {
    addDPLinkToSiteMenu({
      editorSdkProxy,
      pageRef: result.newPageRef,
      innerRoute: route,
      label: menuItemLabel,
    })
  }

  const label = isEditor3(editorType)
    ? UNDO_REDO_LABELS.UPDATE_ROUTERS
    : UNDO_REDO_LABELS.ADD_DATA_BINDING_URL

  const addHistoryLabel = editorSdkProxy.history.add({
    label,
    params: { pageRefs: [result.newPageRef] },
  })

  const [collection] = await Promise.all([
    collectionsApi.get(collectionId),
    addHistoryLabel,
  ])

  logger.bi(
    dynamicPageCustomRouterCreatePageClicked({
      ...defaultPanelBiParams,
      page_type: isStaticRoute ? PAGE_TYPES.COLLECTION : PAGE_TYPES.ITEM,
      collection_id: collectionId,
      collection_name: collection.displayName,
      collection_type: getCollectionType(collection),
      new_url: `${routerPrefix}${route}`,
      is_auto_bound: null,
      new_collection: false,
      pageId: result.newPageRef.id,
      action_type: 'duplicate_dynamic_page',
      origin,
    }),
  )

  return result
}

const getProgressBarOptions = ({ editorType, i18n }) => {
  if (isEditor3(editorType)) {
    return {
      title: i18n.t('Duplicating_Dynamic_Page_Progress_Bar_Title_Editor3'),
      stepTitle: i18n.t(
        'Duplicating_Dynamic_Page_Progress_Bar_Step_Title_Editor3',
      ),
      hideNumericIndicator: true,
    }
  }
  return {
    title: i18n.t('Duplicating_Dynamic_Page_Progress_Bar_Title'),
  }
}

export const duplicateDynamicPage = async ({
  pageRef,
  origin,
  editorSdkProxy,
  httpClient,
  collectionsApi,
  logger,
  i18n,
  editorType,
  fedopsLogger,
  experimentsManager,
  appApiPromise,
}) => {
  const fedopsInteractionName =
    getFedopsInteractionName(editorType)('duplicate')

  fedopsLogger.interactionStarted(fedopsInteractionName)

  const { newPageRef } = await withProgressBar(
    {
      editorSdkProxy,
      ...getProgressBarOptions({ editorType, i18n }),
    },
    () =>
      duplicatePage({
        editorSdkProxy,
        httpClient,
        pageRef,
        origin,
        collectionsApi,
        logger,
        i18n,
        experimentsManager,
        appApiPromise,
        editorType,
      }),
  )

  const pageType = await getDynamicPageType({
    editorSdkProxy,
    pageRef,
  })

  if (pageType) {
    await setDynamicPageTypeStateForPage({
      editorSdkProxy,
      pageRef: newPageRef,
      pageType,
    })
  }

  fedopsLogger.interactionEnded(fedopsInteractionName)

  if (!isEditor3(editorType)) {
    await editorSdkProxy.editor.openPagesPanel({
      pageRef: newPageRef,
      renameEnabled: true,
    })
  }
}
